import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Star, Eye, Heart, User, Calendar } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const SpotlightPage = () => {
  const { api } = useAuth();
  const [spotlights, setSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpotlights = async () => {
      try {
        const res = await api.get('/spotlight');
        setSpotlights(res.data);
      } catch (error) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };

    fetchSpotlights();
  }, [api]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="spotlight-page">
      {/* Header */}
      <div className="relative mb-12">
        <div className="absolute inset-0 rounded-2xl overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1674476803343-2a2c7678d367"
            alt="Spotlight Works"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-tertiary/90 to-brand-accent/70" />
        </div>
        <div className="relative p-8 md:p-12 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Star className="w-6 h-6 text-white" />
            <span className="overline text-white/90">Weekly Selections</span>
          </div>
          <h1 className="font-serif text-4xl font-semibold text-white mb-4">Spotlight Works</h1>
          <p className="text-white/80 max-w-xl mx-auto">
            Every week, Heather and Rachelle each select a piece that particularly touched them 
            to share with the community.
          </p>
        </div>
      </div>

      {/* Spotlights */}
      {loading ? (
        <div className="space-y-8">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      ) : spotlights.length === 0 ? (
        <div className="text-center py-20">
          <Star className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
            No spotlight works yet
          </h2>
          <p className="text-text-secondary">
            Check back soon for featured stories handpicked by our founders!
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {spotlights.map((spotlight, index) => (
            <Link 
              key={spotlight.id} 
              to={`/story/${spotlight.story_id}`}
              className="block group"
              data-testid={`spotlight-${spotlight.id}`}
            >
              <Card className="bg-gradient-to-br from-brand-tertiary/5 to-brand-accent/5 border-brand-tertiary/20 overflow-hidden card-hover">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {spotlight.story?.cover_image && (
                      <div className="md:w-1/3 h-48 md:h-auto overflow-hidden">
                        <img 
                          src={spotlight.story.cover_image}
                          alt={spotlight.story_title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className={`flex-1 p-8 ${!spotlight.story?.cover_image ? 'md:col-span-2' : ''}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-brand-tertiary/20 text-brand-tertiary">
                          <Star className="w-4 h-4" />
                          <span className="text-sm font-medium">Selected by {spotlight.selector_name}</span>
                        </div>
                        <span className="text-sm text-text-muted flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Week of {new Date(spotlight.week_start).toLocaleDateString()}
                        </span>
                      </div>

                      <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2 group-hover:text-brand-accent transition-colors">
                        {spotlight.story_title}
                      </h2>

                      <p className="text-text-secondary mb-4 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        by {spotlight.story_author_name}
                      </p>

                      {spotlight.note && (
                        <blockquote className="border-l-4 border-brand-tertiary pl-4 italic text-text-secondary mb-4">
                          "{spotlight.note}"
                        </blockquote>
                      )}

                      {spotlight.story && (
                        <>
                          {spotlight.story.summary && (
                            <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                              {spotlight.story.summary}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-text-muted">
                            <span className="flex items-center gap-1">
                              <Eye className="w-4 h-4" /> {spotlight.story.view_count} views
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-4 h-4" /> {spotlight.story.like_count} likes
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                              {spotlight.story.genre}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SpotlightPage;
